import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';

import { UserService, canUploadAllFolders, isReadAllOnly } from '../../services/user.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  private readonly auth = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly host = inject(ElementRef<HTMLElement>);

  // Identity (name/email) comes from the Auth0 ID token. /api/me only
  // contributes server-side data, used here to decide whether to show Reports.
  private readonly auth0User = toSignal(this.auth.user$, { initialValue: null });

  readonly user = computed(() => {
    const a0 = this.auth0User();
    if (!a0) return null;
    return { name: a0.name ?? '', email: a0.email ?? '' };
  });

  // Hidden for a read-all-only user, who has no reports. Shown until /api/me
  // loads; the route guard covers a direct visit.
  readonly showReports = computed(() => !isReadAllOnly(this.userService.user()));

  // Staff with the upload permission only; hidden until /api/me says so.
  readonly showUpload = computed(() => canUploadAllFolders(this.userService.user()));

  readonly menuOpen = signal(false);

  readonly initials = computed(() => {
    const u = this.user();
    const source = u?.name || u?.email || '';
    if (!source) return '';
    const parts = source.split(/[\s@._-]+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  });

  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  logout(): void {
    this.closeMenu();
    this.userService.clear();
    this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.closeMenu();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.menuOpen()) this.closeMenu();
  }
}
